import { useState, useEffect } from 'react';
import { useStore } from '../store/useStore';
import { getStoredLicense, checkKeyFormat, saveStoredLicense, getDeviceId, PlanType, LicenseInfo, generateTransferCode, activateWithTransferCode } from '../lib/license';

const PLAN_FEATURES: Record<string, { companies: string; gst: boolean; payroll: boolean; bank: boolean; export: boolean; validity: string }> = {
  demo:     { companies: 'Unlimited (Trial)', gst: true,  payroll: true,  bank: true,  export: true,  validity: '7 days' },
  basic:    { companies: '1',                 gst: false, payroll: false, bank: false, export: false, validity: '1 Year' },
  pro:      { companies: '3',                 gst: true,  payroll: true,  bank: true,  export: true,  validity: '1 Year' },
  unlimited:{ companies: 'Unlimited',         gst: true,  payroll: true,  bank: true,  export: true,  validity: '1 Year' },
};

const PLAN_LABELS: Record<string, string> = {
  demo: 'Trial (7 Days)', basic: 'Basic', pro: 'Pro', unlimited: 'Unlimited'
};

const PLAN_COLOR: Record<string, string> = {
  demo: '#b45309', basic: '#1e8585', pro: '#5b21b6', unlimited: '#065f46'
};

const Tick = () => <span style={{ color: '#1e8585', fontWeight: 700 }}>✓</span>;
const Cross = () => <span style={{ color: '#9ca3af' }}>—</span>;

export const LicensePanelModal = () => {
  const showLicensePanel = useStore(s => s.showLicensePanel);
  const setShowLicensePanel = useStore(s => s.setShowLicensePanel);
  const setLicenseInfo = useStore(s => s.setLicenseInfo);

  const [license, setLicense] = useState<LicenseInfo | null>(null);
  const [tab, setTab] = useState<'info' | 'activate' | 'transfer'>('info');
  const [newKey, setNewKey] = useState('');
  const [transferCode, setTransferCode] = useState('');
  const [transferInput, setTransferInput] = useState('');
  const [transferKeyInput, setTransferKeyInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState<{ text: string; ok: boolean } | null>(null);

  useEffect(() => {
    if (showLicensePanel) {
      setLicense(getStoredLicense());
      setTab('info');
      setNewKey('');
      setMsg(null);
    }
  }, [showLicensePanel]);

  useEffect(() => {
    if (!showLicensePanel) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        setShowLicensePanel(false);
      }
    };
    window.addEventListener('keydown', onKeyDown, true);
    return () => window.removeEventListener('keydown', onKeyDown, true);
  }, [showLicensePanel, setShowLicensePanel]);

  if (!showLicensePanel) return null;

  const plan = license?.plan || 'free';
  const planKey = license?.type === 'demo' ? 'demo' : plan;
  const features = PLAN_FEATURES[planKey] || PLAN_FEATURES['basic'];
  const planLabel = PLAN_LABELS[planKey] || plan;
  const planColor = PLAN_COLOR[planKey] || '#1e8585';

  const expiryDate = license?.expiresAt ? new Date(license.expiresAt) : null;
  const now = Date.now();
  const daysLeft = expiryDate ? Math.ceil((expiryDate.getTime() - now) / 86400000) : null;
  const isExpired = daysLeft !== null && daysLeft <= 0;
  const isExpiringSoon = daysLeft !== null && daysLeft > 0 && daysLeft <= 7;

  const handleActivate = async () => {
    setMsg(null);
    const key = newKey.trim().toUpperCase();
    if (key.length < 5) { setMsg({ text: 'Please enter a valid license key.', ok: false }); return; }
    setLoading(true);
    try {
      const result = await checkKeyFormat(key);
      if (result.valid) {
        const updated: LicenseInfo = {
          type: 'full',
          plan: result.plan as PlanType,
          activatedAt: new Date().toISOString(),
          expiresAt: result.expiresAt ?? null,
          key,
          customer: result.customer,
          deviceId: getDeviceId(),
        };
        saveStoredLicense(updated);
        setLicenseInfo(updated);
        setLicense(updated);
        setMsg({ text: `✓ License activated! Plan: ${result.plan?.toUpperCase()}`, ok: true });
        setNewKey('');
        setTimeout(() => setTab('info'), 1200);
      } else {
        setMsg({ text: result.reason || 'Invalid or expired license key.', ok: false });
      }
    } catch { setMsg({ text: 'Network error. Please check connection.', ok: false }); }
    finally { setLoading(false); }
  };

  // Styles
  const s = {
    overlay: { position: 'fixed' as const, inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 9000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' },
    modal: { background: '#fff', borderRadius: 8, width: '100%', maxWidth: 480, boxShadow: '0 20px 60px rgba(0,0,0,0.25)', overflow: 'hidden' },
    header: { background: '#1e8585', padding: '14px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' },
    tab: (active: boolean) => ({ padding: '6px 16px', fontSize: 12, fontWeight: 600, cursor: 'pointer', border: 'none', borderBottom: active ? '2px solid #1e8585' : '2px solid transparent', background: 'none', color: active ? '#1e8585' : '#64748b' }),
    badge: { display: 'inline-block', padding: '2px 10px', borderRadius: 12, fontSize: 11, fontWeight: 700, background: planColor + '18', color: planColor, border: `1px solid ${planColor}40`, letterSpacing: 0.5 },
    row: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '7px 0', borderBottom: '1px solid #f1f5f9', fontSize: 13 },
    label: { color: '#64748b' },
    val: { color: '#1e293b', fontWeight: 500 },
    input: (err: boolean) => ({ width: '100%', padding: '9px 12px', border: `1.5px solid ${err ? '#ef4444' : '#cbd5e1'}`, borderRadius: 6, fontSize: 13, fontFamily: 'monospace', letterSpacing: 1.5, outline: 'none' }),
    btnPrimary: { width: '100%', padding: '9px', background: '#1e8585', color: '#fff', border: 'none', borderRadius: 6, fontSize: 13, fontWeight: 700, cursor: 'pointer', marginTop: 10 },
    btnSecondary: { padding: '5px 14px', background: '#f1f5f9', color: '#334155', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: 'pointer' },
  };

  return (
    <div style={s.overlay} onClick={() => setShowLicensePanel(false)}>
      <div style={s.modal} onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div style={s.header}>
          <div>
            <div style={{ color: '#fff', fontWeight: 700, fontSize: 14 }}>License & Setup</div>
            <div style={{ color: 'rgba(255,255,255,0.7)', fontSize: 11, marginTop: 1 }}>Manage your Aarthika Pro subscription</div>
          </div>
          <button onClick={() => setShowLicensePanel(false)} style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.8)', fontSize: 20, cursor: 'pointer', lineHeight: 1 }}>✕</button>
        </div>

        {/* Tabs */}
        <div style={{ display: 'flex', borderBottom: '1px solid #e2e8f0', padding: '0 20px' }}>
          <button style={s.tab(tab === 'info')} onClick={() => setTab('info')}>Current License</button>
          <button style={s.tab(tab === 'activate')} onClick={() => setTab('activate')}>
            {isExpired ? '🔴 Activate / Renew' : '+ Activate / Upgrade'}
          </button>
          <button style={s.tab(tab === 'transfer')} onClick={() => setTab('transfer')}>↔ Transfer</button>
        </div>

        <div style={{ padding: '20px' }}>

          {/* ── INFO TAB ── */}
          {tab === 'info' && (
            <div>
              {/* Plan badge + expiry alert */}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
                <div>
                  <div style={{ fontSize: 11, color: '#94a3b8', marginBottom: 4, textTransform: 'uppercase', letterSpacing: 0.5 }}>Active Plan</div>
                  <span style={s.badge}>{planLabel}</span>
                </div>
                {daysLeft !== null && (
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontSize: 11, color: '#94a3b8', marginBottom: 2 }}>
                      {isExpired ? 'EXPIRED' : 'EXPIRES IN'}
                    </div>
                    <div style={{ fontSize: 18, fontWeight: 700, color: isExpired ? '#dc2626' : isExpiringSoon ? '#d97706' : '#1e8585' }}>
                      {isExpired ? 'Expired' : `${daysLeft} day${daysLeft !== 1 ? 's' : ''}`}
                    </div>
                    {expiryDate && <div style={{ fontSize: 10, color: '#94a3b8' }}>{expiryDate.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}</div>}
                  </div>
                )}
                {daysLeft === null && <div style={{ fontSize: 12, color: '#1e8585', fontWeight: 600 }}>♾ Lifetime</div>}
              </div>

              {/* Expiry warning banner */}
              {(isExpired || isExpiringSoon) && (
                <div style={{ background: isExpired ? '#fef2f2' : '#fefce8', border: `1px solid ${isExpired ? '#fca5a5' : '#fde68a'}`, borderRadius: 6, padding: '8px 12px', marginBottom: 14, fontSize: 12, color: isExpired ? '#b91c1c' : '#92400e', lineHeight: 1.6 }}>
                  {isExpired
                    ? '⛔ Your license has expired. Activate a new key to continue using Aarthika Pro.'
                    : `⚠ Your license expires soon. Activate a new key to avoid interruption.`}
                  <button onClick={() => setTab('activate')} style={{ marginLeft: 8, color: isExpired ? '#b91c1c' : '#92400e', fontWeight: 700, background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline', fontSize: 12, padding: 0 }}>
                    Activate now →
                  </button>
                </div>
              )}

              {/* License details */}
              <div style={{ marginBottom: 16 }}>
                {license?.customer && (
                  <div style={s.row}><span style={s.label}>Customer</span><span style={s.val}>{license.customer}</span></div>
                )}
                {license?.key && (
                  <div style={s.row}><span style={s.label}>License Key</span><span style={{ ...s.val, fontFamily: 'monospace', fontSize: 11, letterSpacing: 1, color: '#475569' }}>{license.key}</span></div>
                )}
                <div style={s.row}><span style={s.label}>Type</span><span style={s.val}>{license?.type === 'demo' ? 'Trial' : 'Full License'}</span></div>
                {license?.activatedAt && (
                  <div style={s.row}><span style={s.label}>Activated On</span><span style={s.val}>{new Date(license.activatedAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}</span></div>
                )}
              </div>

              {/* Feature grid */}
              <div style={{ background: '#f8fafc', borderRadius: 6, border: '1px solid #e2e8f0', overflow: 'hidden', marginBottom: 16 }}>
                <div style={{ padding: '6px 12px', fontSize: 10, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.5, borderBottom: '1px solid #e2e8f0' }}>Plan Features</div>
                {[
                  ['Companies', features.companies],
                  ['GST / TDS', features.gst ? true : false],
                  ['Payroll & HR', features.payroll ? true : false],
                  ['Bank Import', features.bank ? true : false],
                  ['Excel / PDF Export', features.export ? true : false],
                  ['Validity', features.validity],
                ].map(([lbl, val]) => (
                  <div key={lbl as string} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 12px', borderBottom: '1px solid #f1f5f9', fontSize: 12 }}>
                    <span style={{ color: '#475569' }}>{lbl}</span>
                    <span style={{ fontWeight: 600, color: '#1e293b' }}>{typeof val === 'boolean' ? (val ? <Tick /> : <Cross />) : val}</span>
                  </div>
                ))}
              </div>

              <button onClick={() => setTab('activate')} style={{ ...s.btnPrimary, background: isExpired ? '#dc2626' : '#1e8585' }}>
                {isExpired ? 'Renew License' : plan === 'basic' ? 'Upgrade to Pro' : 'Activate / Change License'}
              </button>
            </div>
          )}

          {/* ── ACTIVATE TAB ── */}
          {tab === 'activate' && (
            <div>
              <p style={{ fontSize: 13, color: '#475569', marginBottom: 16, lineHeight: 1.7 }}>
                Enter your license key to activate or upgrade your plan. Contact your vendor to purchase a new key.
              </p>

              <div style={{ marginBottom: 8 }}>
                <label style={{ fontSize: 11, fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.5, display: 'block', marginBottom: 6 }}>License Key</label>
                <input
                  type="text"
                  placeholder="XXXX-XXXX-XXXX-XXXX"
                  value={newKey}
                  onChange={e => { setNewKey(e.target.value.toUpperCase()); setMsg(null); }}
                  disabled={loading}
                  style={s.input(msg !== null && !msg.ok)}
                  autoFocus
                />
              </div>

              {msg && (
                <div style={{ padding: '8px 12px', borderRadius: 6, fontSize: 12, fontWeight: 500, background: msg.ok ? '#f0fdf4' : '#fef2f2', color: msg.ok ? '#166534' : '#b91c1c', border: `1px solid ${msg.ok ? '#bbf7d0' : '#fca5a5'}`, marginBottom: 4 }}>
                  {msg.text}
                </div>
              )}

              <button onClick={handleActivate} disabled={loading} style={{ ...s.btnPrimary, opacity: loading ? 0.6 : 1, cursor: loading ? 'not-allowed' : 'pointer' }}>
                {loading ? 'Validating...' : 'Activate License'}
              </button>

              {/* Plan comparison */}
              <div style={{ marginTop: 20, background: '#f8fafc', borderRadius: 6, border: '1px solid #e2e8f0', overflow: 'hidden' }}>
                <div style={{ padding: '6px 12px', fontSize: 10, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.5, borderBottom: '1px solid #e2e8f0' }}>Plan Comparison</div>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                  <thead>
                    <tr style={{ background: '#f1f5f9' }}>
                      <th style={{ textAlign: 'left', padding: '6px 12px', color: '#64748b', fontWeight: 600 }}>Feature</th>
                      <th style={{ textAlign: 'center', padding: '6px 8px', color: '#64748b', fontWeight: 600 }}>Basic</th>
                      <th style={{ textAlign: 'center', padding: '6px 8px', color: '#5b21b6', fontWeight: 700, background: '#f5f3ff' }}>Pro ★</th>
                      <th style={{ textAlign: 'center', padding: '6px 8px', color: '#065f46', fontWeight: 600 }}>Unlimited</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[
                      ['Companies', '1', '3', '∞'],
                      ['GST / TDS', false, true, true],
                      ['Payroll', false, true, true],
                      ['Bank Import', false, true, true],
                      ['Validity', '1 yr', '1 yr', '1 yr'],
                    ].map(([lbl, b, p, u]) => (
                      <tr key={lbl as string} style={{ borderTop: '1px solid #f1f5f9' }}>
                        <td style={{ padding: '5px 12px', color: '#334155' }}>{lbl}</td>
                        <td style={{ textAlign: 'center', padding: '5px 8px' }}>{typeof b === 'boolean' ? (b ? <Tick /> : <Cross />) : b}</td>
                        <td style={{ textAlign: 'center', padding: '5px 8px', background: '#f5f3ff', fontWeight: 600, color: '#5b21b6' }}>{typeof p === 'boolean' ? (p ? <Tick /> : <Cross />) : p}</td>
                        <td style={{ textAlign: 'center', padding: '5px 8px' }}>{typeof u === 'boolean' ? (u ? <Tick /> : <Cross />) : u}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* ── TRANSFER TAB ── */}
          {tab === 'transfer' && (
            <div style={{ padding: '20px 24px' }}>
              {license?.key ? (
                <div>
                  <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 12, color: '#0f172a' }}>Transfer License to Another PC</div>
                  <p style={{ fontSize: 12, color: '#64748b', lineHeight: 1.6, marginBottom: 16 }}>
                    This will deactivate your license on this PC and generate a 6-digit code.
                    Enter this code on the new PC to activate. Code expires in 24 hours.
                  </p>
                  {transferCode ? (
                    <div style={{ textAlign: 'center', padding: 20, background: '#f0fdf4', border: '2px solid #86efac', borderRadius: 10 }}>
                      <div style={{ fontSize: 11, color: '#16a34a', fontWeight: 600, marginBottom: 8 }}>TRANSFER CODE (share with new PC)</div>
                      <div style={{ fontSize: 32, fontWeight: 900, letterSpacing: 8, color: '#15803d', fontFamily: 'monospace' }}>{transferCode}</div>
                      <div style={{ fontSize: 11, color: '#64748b', marginTop: 8 }}>Valid for 24 hours. License deactivated from this PC.</div>
                      <button onClick={() => { navigator.clipboard.writeText(transferCode); setMsg({ text: 'Code copied!', ok: true }); }}
                        style={{ marginTop: 12, padding: '6px 16px', background: '#16a34a', color: '#fff', border: 'none', borderRadius: 6, fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
                        Copy Code
                      </button>
                    </div>
                  ) : (
                    <button onClick={async () => {
                      setLoading(true); setMsg(null);
                      const result = await generateTransferCode();
                      setLoading(false);
                      if (result.success && result.code) {
                        setTransferCode(result.code);
                        setLicense(null);
                        setLicenseInfo(null);
                      } else {
                        setMsg({ text: result.reason || 'Transfer failed', ok: false });
                      }
                    }} disabled={loading}
                      style={{ ...s.btnPrimary, background: '#b45309', width: '100%' }}>
                      {loading ? 'Generating...' : '↔ Generate Transfer Code & Deactivate'}
                    </button>
                  )}
                </div>
              ) : (
                <div>
                  <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 12, color: '#0f172a' }}>Activate with Transfer Code</div>
                  <p style={{ fontSize: 12, color: '#64748b', lineHeight: 1.6, marginBottom: 16 }}>
                    If you transferred a license from another PC, enter the license key and the 6-digit code below.
                  </p>
                  <div style={{ marginBottom: 10 }}>
                    <label style={{ fontSize: 11, fontWeight: 600, color: '#64748b', display: 'block', marginBottom: 4 }}>LICENSE KEY</label>
                    <input value={transferKeyInput} onChange={e => setTransferKeyInput(e.target.value.toUpperCase())}
                      placeholder="XXXX-XXXX-XXXX-XXXX"
                      style={{ width: '100%', padding: '10px 12px', border: '1.5px solid #e2e8f0', borderRadius: 8, fontSize: 14, fontFamily: 'monospace', letterSpacing: 2, textAlign: 'center', outline: 'none' }} />
                  </div>
                  <div style={{ marginBottom: 14 }}>
                    <label style={{ fontSize: 11, fontWeight: 600, color: '#64748b', display: 'block', marginBottom: 4 }}>TRANSFER CODE</label>
                    <input value={transferInput} onChange={e => setTransferInput(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      placeholder="6-digit code"
                      style={{ width: '100%', padding: '10px 12px', border: '1.5px solid #e2e8f0', borderRadius: 8, fontSize: 20, fontFamily: 'monospace', letterSpacing: 6, textAlign: 'center', outline: 'none' }}
                      maxLength={6} />
                  </div>
                  <button onClick={async () => {
                    if (!transferKeyInput.trim() || !transferInput.trim()) { setMsg({ text: 'Enter both license key and transfer code', ok: false }); return; }
                    setLoading(true); setMsg(null);
                    const result = await activateWithTransferCode(transferKeyInput, transferInput);
                    setLoading(false);
                    if (result.success) {
                      setMsg({ text: 'License transferred successfully! Restart the app.', ok: true });
                      setLicense(getStoredLicense());
                      setLicenseInfo(getStoredLicense());
                    } else {
                      setMsg({ text: result.reason || 'Transfer failed', ok: false });
                    }
                  }} disabled={loading}
                    style={{ ...s.btnPrimary, width: '100%' }}>
                    {loading ? 'Verifying...' : 'Activate with Transfer Code'}
                  </button>
                </div>
              )}
              {msg && (
                <div style={{ marginTop: 12, padding: '8px 12px', borderRadius: 6, fontSize: 12, fontWeight: 600, background: msg.ok ? '#f0fdf4' : '#fef2f2', color: msg.ok ? '#16a34a' : '#dc2626', border: `1px solid ${msg.ok ? '#86efac' : '#fecaca'}` }}>
                  {msg.text}
                </div>
              )}
            </div>
          )}

        </div>
      </div>
    </div>
  );
};
